import { useState, useId, ChangeEvent, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";

import "./style.scss";
import { ReactComponent as EyeIcon } from "../../../assets/eye.icon.svg";
import { ReactComponent as EyeOffIcon } from "../../../assets/eye-off.icon.svg";
import { ReactComponent as GoogleIcon } from "../../../assets/google.icon.svg";
import { ReactComponent as MicrosoftIcon } from "../../../assets/microsoft.icon.svg";
import { AuthErrorMessage } from "../../../common/enums/auth-error-message.enum";
import { FirebaseErrorCode } from "../../../common/enums/firebase-error-code.enum";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { emailRegex } from "../../../common/utilities/constants/constants";
import { AuthTemplate } from "../../../components/auth-template/auth-template.comp";
import { Loader, LoaderView } from "../../../components/loader/loader.comp";
import { validateReturnUrl } from "../../../common/utilities/auth-domain/return-url-validator.util";
import {
    parseReturnUrl,
    constructCallbackUrl
} from "../../../common/utilities/auth-domain/auth-url.util";

const AuthDomainSignUp = () => {
    // Clear all storage when navigating to registration page.
    // Preserve the tenant origin: it is written during render (below) and is required
    // by FirebaseAuthContainer to resolve the Firebase tenant before OAuth. Clearing it
    // here would race the tenant fetch and produce a default-pool (tenant-less) user.
    useEffect(() => {
        const preservedTenantOrigin = sessionStorage.getItem("auth_tenant_origin");
        sessionStorage.clear();
        localStorage.clear();
        if (preservedTenantOrigin) {
            sessionStorage.setItem("auth_tenant_origin", preservedTenantOrigin);
        }
    }, []);

    const emailMask = new RegExp(process.env.REACT_APP_EMAIL_REGEX ?? emailRegex, "g");
    const [searchParams] = useSearchParams();

    const returnUrl = parseReturnUrl(searchParams);
    const invite = searchParams.get("invite");
    const tenantOrigin = searchParams.get("tenantOrigin");

    // Store tenantOrigin immediately when available to avoid race condition with FirebaseAuthContainer
    // This must happen before FirebaseAuthContainer.useContainer() is called
    // Always update if tenantOrigin is provided in URL
    if (tenantOrigin) {
        sessionStorage.setItem("auth_tenant_origin", tenantOrigin);
    }

    const { signUp, signInWithGoogle, signInWithMicrosoft, providerLoading, isTrial } =
        FirebaseAuthContainer.useContainer();

    // Check if tenant is initialized (isTrial will be undefined until tenant loads)
    const tenantReady = isTrial !== undefined;

    // Check if we're in an OAuth popup (opened by Firebase)
    // OAuth popup will have window.opener but no returnUrl (Firebase redirects without preserving params)
    const isOAuthPopup = !!window.opener && !returnUrl;

    // Debug logging
    console.log("AuthDomainSignUp - Query params:", {
        returnUrl,
        invite,
        tenantOrigin,
        currentUrl: window.location.href,
        isOAuthPopup,
        windowName: window.name,
        hasOpener: !!window.opener
    });

    // Build login link preserving all params
    const buildLoginLink = () => {
        const params = new URLSearchParams();
        if (returnUrl) params.set("returnUrl", returnUrl);
        if (tenantOrigin) params.set("tenantOrigin", tenantOrigin);
        if (invite) params.set("invite", invite);
        return `/login?${params.toString()}`;
    };
    const loginLink = buildLoginLink();

    const [login, setLogin] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [passwordType, setPasswordType] = useState<string>("password");
    const [confirmPasswordType, setConfirmPasswordType] = useState<string>("password");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isInvalid, setIsInvalid] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string>("");
    const errorId = useId();

    // Validate return URL on mount and store in sessionStorage for OAuth flow
    useEffect(() => {
        // Skip validation if we're in an OAuth popup - Firebase handles this
        if (isOAuthPopup) {
            console.log("In OAuth popup, skipping validation");
            return;
        }

        if (!returnUrl) {
            // Check if returnUrl is in sessionStorage (from OAuth redirect)
            const storedReturnUrl = sessionStorage.getItem("auth_return_url");
            if (!storedReturnUrl) {
                setValidationError("Missing return URL. Please navigate from your tenant domain.");
                return;
            }
        } else {
            // Store returnUrl in sessionStorage for OAuth popup flow
            sessionStorage.setItem("auth_return_url", returnUrl);

            // tenantOrigin is already stored at component initialization to avoid race condition

            const validation = validateReturnUrl(returnUrl, window.location.hostname);
            if (!validation.isValid) {
                setValidationError(validation.error || "Invalid return URL");
            }
        }
    }, [returnUrl, tenantOrigin, isOAuthPopup]);

    const handleLoginChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setLogin(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handlePasswordChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setPassword(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handleConfirmPasswordChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setConfirmPassword(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handlePasswordEyeClick = () => {
        setPasswordType(prevValue => (prevValue === "text" ? "password" : "text"));
    };

    const handleConfirmPasswordEyeClick = () => {
        setConfirmPasswordType(prevValue => (prevValue === "text" ? "password" : "text"));
    };

    const handleSignUpClick = async () => {
        if (!login || !password || !confirmPassword || !returnUrl || validationError) return;

        if (!login.match(emailMask)) {
            setIsInvalid(true);
            setErrorMessage(AuthErrorMessage.invalidEmail);
            return;
        } else if (password !== confirmPassword) {
            setIsInvalid(true);
            setErrorMessage(AuthErrorMessage.differentPasswords);
            return;
        } else if (!invite) {
            setIsInvalid(true);
            setErrorMessage(AuthErrorMessage.invalidInvite);
            return;
        }

        setIsLoading(true);

        try {
            const response = await signUp(login, password, invite);
            const token = await response.user.getIdToken();
            const refreshToken = response.user.refreshToken;

            // Redirect to tenant with tokens
            const callbackUrl = constructCallbackUrl(
                returnUrl,
                { accessToken: token, refreshToken },
                true
            );

            window.location.href = callbackUrl;
        } catch (error: any) {
            switch (error.code as FirebaseErrorCode) {
                case FirebaseErrorCode.emailAlreadyInUse:
                    setIsInvalid(true);
                    setErrorMessage(AuthErrorMessage.emailAlreadyInUse);
                    break;
                case FirebaseErrorCode.weakPassword:
                    setIsInvalid(true);
                    setErrorMessage(AuthErrorMessage.weakPassword);
                    break;
                case FirebaseErrorCode.invalidInvite:
                    setIsInvalid(true);
                    setErrorMessage(AuthErrorMessage.invalidInvite);
                    break;
                case FirebaseErrorCode.inappropriateInvite:
                    setIsInvalid(true);
                    setErrorMessage(AuthErrorMessage.inappropriateInvite);
                    break;
                default:
                    setErrorMessage(AuthErrorMessage.default);
            }
            setIsLoading(false);
        }
    };

    const handleSignInWithGoogleClick = async () => {
        if (isLoading || validationError) return;

        try {
            const response = await signInWithGoogle(invite);
            const token = await response.user.getIdToken();
            const refreshToken = response.user.refreshToken;
            const userEmail = response.user.email;

            // Get returnUrl from sessionStorage or query params
            const finalReturnUrl = returnUrl || sessionStorage.getItem("auth_return_url");

            if (!finalReturnUrl) {
                setErrorMessage("Missing return URL. Please try again.");
                return;
            }

            if (!userEmail) {
                setErrorMessage("User email not found. Please try again.");
                return;
            }

            // Clear sessionStorage
            sessionStorage.removeItem("auth_return_url");

            // Redirect to tenant with tokens, email, invite, and Firebase UID for OAuth provisioning
            const callbackUrl = constructCallbackUrl(
                finalReturnUrl,
                { accessToken: token, refreshToken },
                true,
                undefined,
                userEmail,
                invite,
                response.user.uid
            );

            window.location.href = callbackUrl;
        } catch (error: any) {
            providerErrorHandler(error);
        }
    };

    const handleSignInWithMicrosoftClick = async () => {
        if (isLoading || validationError) return;

        try {
            const response = await signInWithMicrosoft(invite);
            const token = await response.user.getIdToken();
            const refreshToken = response.user.refreshToken;
            const userEmail = response.user.email;

            // Get returnUrl from sessionStorage or query params
            const finalReturnUrl = returnUrl || sessionStorage.getItem("auth_return_url");

            if (!finalReturnUrl) {
                setErrorMessage("Missing return URL. Please try again.");
                return;
            }

            if (!userEmail) {
                setErrorMessage("User email not found. Please try again.");
                return;
            }

            // Clear sessionStorage
            sessionStorage.removeItem("auth_return_url");

            // Redirect to tenant with tokens, email, invite, and Firebase UID for OAuth provisioning
            const callbackUrl = constructCallbackUrl(
                finalReturnUrl,
                { accessToken: token, refreshToken },
                true,
                undefined,
                userEmail,
                invite,
                response.user.uid
            );

            window.location.href = callbackUrl;
        } catch (error: any) {
            providerErrorHandler(error);
        }
    };

    const providerErrorHandler = (error: any) => {
        switch (error.code as FirebaseErrorCode) {
            case FirebaseErrorCode.closePopup:
            case FirebaseErrorCode.cancelPopup:
                break;
            case FirebaseErrorCode.invalidInvite:
                setErrorMessage(AuthErrorMessage.invalidInvite);
                return;
            case FirebaseErrorCode.inappropriateInvite:
                setErrorMessage(AuthErrorMessage.inappropriateInvite);
                return;
            case FirebaseErrorCode.invalidEmail:
                setErrorMessage(AuthErrorMessage.invalidEmail);
                return;
            case FirebaseErrorCode.forbiddenInvite:
                setErrorMessage(AuthErrorMessage.forbiddenInvite);
                return;
            case FirebaseErrorCode.invalidProvider:
                setErrorMessage(AuthErrorMessage.invalidProvider);
                return;
            case FirebaseErrorCode.accessPermissions:
                setErrorMessage(AuthErrorMessage.lackPermissions);
                return;
            default:
                setErrorMessage(AuthErrorMessage.default);
        }
    };

    // If we're in an OAuth popup, show loading state (Firebase will handle communication)
    if (isOAuthPopup) {
        return (
            <AuthTemplate>
                <div className="signup">
                    <div className="signup-title">Completing authentication...</div>
                    <div className="signup-content">
                        <LoaderView show={true} />
                    </div>
                </div>
            </AuthTemplate>
        );
    }

    // Show validation error if return URL is invalid
    if (validationError) {
        return (
            <AuthTemplate>
                <div className="signup">
                    <div className="signup-title">Authentication Error</div>
                    <div className="signup-content">
                        <div
                            className="signup-message"
                            style={{ padding: "20px", textAlign: "center" }}
                        >
                            {validationError}
                        </div>
                        <div
                            style={{
                                padding: "20px",
                                textAlign: "center",
                                fontSize: "14px",
                                color: "#666"
                            }}
                        >
                            Please ensure you navigate to the registration page from your tenant
                            domain.
                        </div>
                    </div>
                </div>
            </AuthTemplate>
        );
    }

    return (
        <AuthTemplate>
            <div className="signup">
                <div className="signup-title" role="heading" aria-level={1}>
                    Sign Up
                </div>
                <div className="signup-description">
                    <span className="signup-description__text">Already have an account?</span>
                    <Link to={loginLink} className="signup-description__link">
                        Log In
                    </Link>
                </div>
                <div className="signup-content">
                    <div className="signup-field">
                        <input
                            className="signup-field__input--required"
                            type="email"
                            aria-label="Email"
                            autoComplete="email"
                            required
                            aria-invalid={errorMessage ? true : undefined}
                            aria-describedby={errorMessage ? errorId : undefined}
                            placeholder="Email"
                            onChange={handleLoginChange}
                            value={login}
                        />
                        <div className="signup-field-settings">
                            <span className="signup-field-settings__required" aria-hidden="true">
                                *
                            </span>
                        </div>
                    </div>
                    <div className="signup-field">
                        <input
                            className="signup-field__input--eye-required"
                            type={passwordType}
                            aria-label="Password"
                            autoComplete="new-password"
                            required
                            aria-invalid={errorMessage ? true : undefined}
                            aria-describedby={errorMessage ? errorId : undefined}
                            placeholder="Password"
                            onChange={handlePasswordChange}
                            value={password}
                        />
                        <div className="signup-field-settings">
                            <button
                                type="button"
                                className="signup-field-settings__eye"
                                aria-label={
                                    passwordType === "text" ? "Hide password" : "Show password"
                                }
                                aria-pressed={passwordType === "text"}
                                onClick={handlePasswordEyeClick}
                            >
                                <SvgIcon
                                    component={passwordType === "text" ? EyeIcon : EyeOffIcon}
                                    inheritViewBox
                                    aria-hidden="true"
                                />
                            </button>
                            <span className="signup-field-settings__required" aria-hidden="true">
                                *
                            </span>
                        </div>
                    </div>
                    <div className="signup-field">
                        <input
                            className="signup-field__input--eye-required"
                            type={confirmPasswordType}
                            aria-label="Confirm Password"
                            autoComplete="new-password"
                            required
                            aria-invalid={errorMessage ? true : undefined}
                            aria-describedby={errorMessage ? errorId : undefined}
                            placeholder="Confirm Password"
                            onChange={handleConfirmPasswordChange}
                            value={confirmPassword}
                        />
                        <div className="signup-field-settings">
                            <button
                                type="button"
                                className="signup-field-settings__eye"
                                aria-label={
                                    confirmPasswordType === "text"
                                        ? "Hide password"
                                        : "Show password"
                                }
                                aria-pressed={confirmPasswordType === "text"}
                                onClick={handleConfirmPasswordEyeClick}
                            >
                                <SvgIcon
                                    component={
                                        confirmPasswordType === "text" ? EyeIcon : EyeOffIcon
                                    }
                                    inheritViewBox
                                    aria-hidden="true"
                                />
                            </button>
                            <span className="signup-field-settings__required" aria-hidden="true">
                                *
                            </span>
                        </div>
                    </div>
                    <div className="signup-message">
                        <span id={errorId} role="alert">
                            {errorMessage}
                        </span>
                    </div>
                    <div className="signin-separator"></div>
                    <div className="signin-providers">
                        <Button
                            className="signin-providers__button"
                            variant="outlined"
                            onClick={handleSignInWithGoogleClick}
                            disabled={!tenantReady}
                            startIcon={<SvgIcon component={GoogleIcon} inheritViewBox />}
                        >
                            Continue with Google
                        </Button>
                        <Button
                            className="signin-providers__button"
                            variant="outlined"
                            onClick={handleSignInWithMicrosoftClick}
                            disabled={!tenantReady}
                            startIcon={<SvgIcon component={MicrosoftIcon} inheritViewBox />}
                        >
                            Continue with Microsoft
                        </Button>
                    </div>
                </div>
                <div className="signup-action">
                    <Button
                        className="primary-button"
                        variant="text"
                        onClick={handleSignUpClick}
                        disabled={!login || !password || !confirmPassword || isLoading || isInvalid}
                    >
                        {!isLoading ? <span>Sign up</span> : <Loader size={24} borderWidth={4} />}
                    </Button>
                </div>
                <LoaderView show={providerLoading} />
            </div>
        </AuthTemplate>
    );
};

export default AuthDomainSignUp;
